import axios from 'axios';

const API_BASE_URL = '/api';

const toServiceError = (error, fallbackMessage) => {
  const payload = error.response?.data;
  const status = error.response?.status;

  if (payload && typeof payload === 'object') {
    const serviceError = new Error(payload.message || fallbackMessage);
    if (status) {
      serviceError.status = status;
    }
    Object.assign(serviceError, payload);
    return serviceError;
  }

  if (error instanceof Error) {
    if (status && error.status == null) {
      error.status = status;
    }
    return error;
  }

  return new Error(fallbackMessage);
};

const isAuthError = (error) => {
  const status = error?.response?.status ?? error?.status;
  return status === 401 || status === 403;
};

const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

const persistAuthSession = (payload) => {
  if (!payload?.access_token) {
    return;
  }

  localStorage.setItem('accessToken', payload.access_token);
  localStorage.setItem('refreshToken', payload.refresh_token);
  localStorage.setItem('user', JSON.stringify(payload.user));
  setAuthToken(payload.access_token);
};

const register = async (userData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/register`, userData);
    return response.data;
  } catch (error) {
    console.error('Registration Error:', error.response?.data || error.message);
    throw toServiceError(error, 'Registration failed');
  }
};

const login = async (credentials) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);
    persistAuthSession(response.data);
    return response.data;
  } catch (error) {
    console.error('Login Error:', error.response?.data || error.message);
    throw toServiceError(error, 'Login failed');
  }
};

const logout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  setAuthToken(null);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:logout'));
  }
};

const refreshToken = async () => {
  try {
    const storedRefreshToken = localStorage.getItem('refreshToken');

    if (!storedRefreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      {
        headers: {
          Authorization: `Bearer ${storedRefreshToken}`,
        },
      },
    );

    if (response.data.access_token) {
      localStorage.setItem('accessToken', response.data.access_token);
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      setAuthToken(response.data.access_token);
      return response.data;
    }

    throw new Error('Invalid response format');
  } catch (error) {
    console.error('Token Refresh Error:', error.response?.data || error.message);
    if (isAuthError(error)) {
      logout();
    }
    return null;
  }
};

const getCurrentUser = () => {
  const serializedUser = localStorage.getItem('user');
  if (!serializedUser) {
    return null;
  }

  try {
    return JSON.parse(serializedUser);
  } catch (error) {
    console.warn('Invalid stored user payload, clearing local cache.');
    localStorage.removeItem('user');
    return null;
  }
};

const getUserProfile = async () => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_BASE_URL}/auth/profile`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    console.error('Get Profile Error:', error.response?.data || error.message);
    throw toServiceError(error, 'Failed to get user profile');
  }
};

const updateUserPreferences = async (preferences) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/preferences`, preferences);
    return response.data;
  } catch (error) {
    console.error('Update Preferences Error:', error.response?.data || error.message);
    throw toServiceError(error, 'Failed to update preferences');
  }
};

const initAuth = () => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    setAuthToken(token);
    return true;
  }
  return false;
};

export {
  register,
  login,
  logout,
  refreshToken,
  getCurrentUser,
  getUserProfile,
  updateUserPreferences,
  initAuth,
  setAuthToken,
  isAuthError,
};
